import { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ResultCard } from "@/components/calculator/ResultCard";
import { PresetButton } from "@/components/calculator/PresetButton";
import { PowerTriangle } from "@/components/calculator/PowerTriangle";
import { Factory, Car, Warehouse } from "lucide-react";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const SQRT3 = Math.sqrt(3);

export const ThreePhaseTab = ({ onSaveHistory }) => {
  const [lineVoltage, setLineVoltage] = useState(480);
  const [lineCurrent, setLineCurrent] = useState(100);
  const [powerFactor, setPowerFactor] = useState(0.85);
  const [powerUnit, setPowerUnit] = useState("kilo");
  
  const debouncedVoltage = useDebounce(lineVoltage, 200);
  const debouncedCurrent = useDebounce(lineCurrent, 200);
  const debouncedPF = useDebounce(powerFactor, 200);

  // Calculations (3Φ AC formulas)
  // P = √3 × V_LL × I_L × PF (in kW)
  // S = √3 × V_LL × I_L (in kVA)
  // Q = √(S² - P²) (in kVAR)
  const apparentPowerKVA = (SQRT3 * debouncedVoltage * debouncedCurrent) / 1000;
  const realPowerKW = apparentPowerKVA * debouncedPF;
  const reactivePowerKVAR = Math.sqrt(Math.pow(apparentPowerKVA, 2) - Math.pow(realPowerKW, 2));

  // Convert to watts for display if needed
  const apparentPowerVA = apparentPowerKVA * 1000;
  const realPowerW = realPowerKW * 1000;
  const reactivePowerVAR = reactivePowerKVAR * 1000;

  // Format power based on unit selection
  const formatPower = useCallback((value, type = "real", isKilo = false) => {
    const useKilo = powerUnit === "kilo";
    const displayValue = useKilo ? (isKilo ? value : value / 1000) : (isKilo ? value * 1000 : value);
    
    let unit;
    switch (type) {
      case "apparent":
        unit = useKilo ? "kVA" : "VA";
        break;
      case "reactive":
        unit = useKilo ? "kVAR" : "VAR";
        break;
      default:
        unit = useKilo ? "kW" : "W";
    }
    
    return { 
      value: displayValue.toFixed(useKilo ? 3 : 2), 
      unit 
    };
  }, [powerUnit]);

  // Save to history
  const handleSave = useCallback(() => {
    const real = formatPower(realPowerKW, "real", true);
    const apparent = formatPower(apparentPowerKVA, "apparent", true);
    const reactive = formatPower(reactivePowerKVAR, "reactive", true);
    
    onSaveHistory({
      type: "Three-Phase AC",
      inputs: {
        lineVoltage: `${debouncedVoltage} V`,
        lineCurrent: `${debouncedCurrent} A`,
        powerFactor: debouncedPF.toFixed(2)
      },
      outputs: {
        realPower: `${real.value} ${real.unit}`,
        apparentPower: `${apparent.value} ${apparent.unit}`,
        reactivePower: `${reactive.value} ${reactive.unit}`
      }
    });
  }, [debouncedVoltage, debouncedCurrent, debouncedPF, realPowerKW, apparentPowerKVA, reactivePowerKVAR, formatPower, onSaveHistory]);

  // Presets
  const presets = [
    { name: "Motor Load", voltage: 480, current: 150, pf: 0.85, icon: Factory },
    { name: "EV Charger", voltage: 480, current: 80, pf: 0.95, icon: Car },
    { name: "Commercial HVAC", voltage: 208, current: 200, pf: 0.88, icon: Warehouse },
  ];

  const applyPreset = (preset) => {
    setLineVoltage(preset.voltage);
    setLineCurrent(preset.current);
    setPowerFactor(preset.pf);
  };

  const real = formatPower(realPowerKW, "real", true);
  const apparent = formatPower(apparentPowerKVA, "apparent", true);
  const reactive = formatPower(reactivePowerKVAR, "reactive", true);

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="flex flex-wrap gap-2">
        {presets.map((preset) => (
          <PresetButton
            key={preset.name}
            label={preset.name}
            icon={preset.icon}
            onClick={() => applyPreset(preset)}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Input Parameters
          </h3>

          {/* Line Voltage */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="three-voltage" className="text-sm font-medium">
                Line Voltage V<sub>LL</sub> (V)
              </Label>
              <div className="flex gap-1">
                {[208, 347, 480, 600].map((v) => (
                  <Button
                    key={v}
                    variant={lineVoltage === v ? "default" : "outline"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setLineVoltage(v)}
                  >
                    {v}V
                  </Button>
                ))}
              </div>
            </div>
            <Input
              id="three-voltage"
              type="number"
              min="0"
              max="15000"
              value={lineVoltage}
              onChange={(e) => setLineVoltage(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
          </div>

          {/* Line Current */}
          <div className="space-y-2">
            <Label htmlFor="three-current" className="text-sm font-medium">
              Line Current I<sub>L</sub> (A)
            </Label>
            <Input
              id="three-current"
              type="number"
              min="0"
              max="10000"
              value={lineCurrent}
              onChange={(e) => setLineCurrent(Number(e.target.value) || 0)}
              className="bg-secondary/50 border-border focus:border-primary"
            />
          </div>

          {/* Power Factor */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                Power Factor (PF)
              </Label>
              <span className="text-sm font-mono text-primary">{powerFactor.toFixed(2)}</span>
            </div>
            <Slider
              value={[powerFactor * 100]}
              onValueChange={(value) => setPowerFactor(value[0] / 100)}
              min={20}
              max={100}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.20 (Lagging)</span>
              <span>1.00 (Unity)</span>
            </div>
            {powerFactor < 0.85 && (
              <p className="text-xs text-warning bg-warning/10 p-2 rounded">
                ⚠️ PF below 0.85 may incur utility penalties
              </p>
            )}
          </div>

          {/* Unit Selection */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Power Display Unit</Label>
            <Select value={powerUnit} onValueChange={setPowerUnit}>
              <SelectTrigger className="bg-secondary/50 border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="base">Base (W, VA, VAR)</SelectItem>
                <SelectItem value="kilo">Kilo (kW, kVA, kVAR)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              Results
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
              className="text-xs"
            >
              Save to History
            </Button>
          </div>

          <div className="space-y-4">
            <ResultCard
              label="Real Power (P)"
              value={real.value}
              unit={real.unit}
              color="real"
              formula="P = √3 × V_LL × I_L × PF / 1000"
            />
            
            <ResultCard
              label="Apparent Power (S)"
              value={apparent.value}
              unit={apparent.unit}
              color="apparent"
              formula="S = √3 × V_LL × I_L / 1000"
            />
            
            <ResultCard
              label="Reactive Power (Q)"
              value={reactive.value}
              unit={reactive.unit}
              color="reactive"
              formula="Q = √(S² - P²)"
            />

            <ResultCard
              label="Power Factor"
              value={debouncedPF.toFixed(2)}
              unit=""
              color="muted"
              formula="PF = P / S"
            />
          </div>

          {/* Power Triangle */}
          <PowerTriangle
            realPower={realPowerW}
            reactivePower={reactivePowerVAR}
            apparentPower={apparentPowerVA}
            powerFactor={debouncedPF}
          />
        </div>
      </div>
    </div>
  );
};
